import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  MagnifyingGlass, ChatCircleDots, Prohibit, PencilSimple, Trash, X
} from "@phosphor-icons/react";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", { params: q ? { search: q } : {} });
      setContacts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const block = async (phone) => {
    try {
      await api.post("/blocklist", { phone_number: phone, reason: "Bloqueado via Contatos" });
      toast.success("Contato bloqueado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const remove = async (phone) => {
    if (!window.confirm(`Excluir contato ${phone}? Isso apaga só o registro do CRM, mensagens são mantidas.`)) return;
    try {
      await api.delete(`/contacts/${encodeURIComponent(phone)}`);
      toast.success("Contato excluído");
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  return (
    <Layout
      title="Contatos"
      subtitle={`${contacts.length} cliente${contacts.length !== 1 ? "s" : ""} salvo${contacts.length !== 1 ? "s" : ""}`}
      action={
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="contacts-search"
              placeholder="Buscar nome ou telefone..."
              className="pl-9 pr-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] w-72"
            />
          </div>
          <button type="submit" className="px-3 py-2 text-sm bg-[#002FA7] text-white hover:bg-[#001f7a]">Buscar</button>
        </form>
      }
    >
      {loading ? (
        <div className="text-sm text-[#64748B]">Carregando contatos…</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E5E7EB] text-[#64748B]">
          <div className="label-mono mb-2">Sem contatos ainda</div>
          <div className="text-sm">Os contatos serão criados automaticamente conforme mensagens chegarem.</div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA] text-left">
                <th className="px-4 py-3 label-mono font-normal">Contato</th>
                <th className="px-4 py-3 label-mono font-normal">Telefone</th>
                <th className="px-4 py-3 label-mono font-normal text-center">Msgs</th>
                <th className="px-4 py-3 label-mono font-normal text-center">Testes</th>
                <th className="px-4 py-3 label-mono font-normal">Última msg</th>
                <th className="px-4 py-3 label-mono font-normal">Tags</th>
                <th className="px-4 py-3 label-mono font-normal text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA] transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name || c.push_name || "—"}</div>
                    {c.notes && <div className="text-xs text-[#64748B] mt-0.5 truncate max-w-xs">{c.notes}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{c.phone_number}</td>
                  <td className="px-4 py-3 text-center">{c.total_messages || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 text-xs ${c.total_trials > 0 ? "bg-[#16A34A]/10 text-[#16A34A]" : "text-[#64748B]"}`}>
                      {c.total_trials || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">
                    {c.last_seen ? new Date(c.last_seen).toLocaleString("pt-BR") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {(c.tags || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => (
                          <span key={t} className="px-1.5 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-xs">{t}</span>
                        ))}
                      </div>
                    ) : <span className="text-[#64748B] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => nav(`/messages?phone=${encodeURIComponent(c.phone_number)}`)}
                        data-testid={`contact-chat-${c.phone_number}`}
                        title="Ver conversa"
                        className="p-1.5 hover:bg-[#E5E7EB] text-[#0F172A]"
                      >
                        <ChatCircleDots size={16} weight="duotone" />
                      </button>
                      <button
                        onClick={() => setEditing(c)}
                        data-testid={`contact-edit-${c.phone_number}`}
                        title="Editar"
                        className="p-1.5 hover:bg-[#E5E7EB] text-[#0F172A]"
                      >
                        <PencilSimple size={16} weight="duotone" />
                      </button>
                      <button
                        onClick={() => block(c.phone_number)}
                        data-testid={`contact-block-${c.phone_number}`}
                        title="Bloquear"
                        className="p-1.5 hover:bg-[#E5E7EB] text-[#DC2626]"
                      >
                        <Prohibit size={16} weight="duotone" />
                      </button>
                      <button
                        onClick={() => remove(c.phone_number)}
                        data-testid={`contact-delete-${c.phone_number}`}
                        title="Excluir"
                        className="p-1.5 hover:bg-[#E5E7EB] text-[#DC2626]"
                      >
                        <Trash size={16} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditContactModal
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(search); }}
        />
      )}
    </Layout>
  );
}

function EditContactModal({ contact, onClose, onSaved }) {
  const [name, setName] = useState(contact.name || "");
  const [notes, setNotes] = useState(contact.notes || "");
  const [tags, setTags] = useState((contact.tags || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/contacts/${encodeURIComponent(contact.phone_number)}`, {
        name,
        notes,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Contato atualizado");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full p-6 border border-[#E5E7EB]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label-mono">Editar contato</div>
            <h2 className="font-display font-bold text-lg tracking-tight">{contact.phone_number}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F8F9FA]"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label-mono block mb-1.5">Nome</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="contact-edit-name"
              className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Tags (separadas por vírgula)</label>
            <input
              type="text" value={tags} onChange={(e) => setTags(e.target.value)}
              data-testid="contact-edit-tags"
              placeholder="vip, lead-quente, parceiro"
              className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Anotações</label>
            <textarea
              rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="contact-edit-notes"
              className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0F172A] hover:bg-[#F8F9FA]">Cancelar</button>
          <button
            onClick={save} disabled={saving}
            data-testid="contact-edit-save"
            className="px-4 py-2 text-sm bg-[#002FA7] text-white hover:bg-[#001f7a] disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
